import type { PoseTimerState, ScheduleItem } from '@modelcast/types';
import { useRoom } from './RoomProvider';
import {
  formatStorageBytes,
  type RecordingStorageEstimate,
  useRecordingStorageEstimate,
} from './useRecordingStorageEstimate';
import {
  getPoseScheduleEntries,
  getPoseScheduleStatusPosition,
  isTransitionBreak,
} from './poseScheduleViewState';

interface PoseSchedulePanelProps {
  poseState: PoseTimerState;
  /** Schedule from unified session state - single source of truth */
  schedule: ScheduleItem[];
  variant?: 'default' | 'modelMobile';
  displayMode?: 'full' | 'timerOnly' | 'scheduleOnly';
  isExpanded?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  onRestart?: () => void;
  onNewPose?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onJumpTo?: (index: number) => void;
  /** If false, hides all control buttons (for artist view) */
  isEditable?: boolean;
  /** Only devices that create source recordings should check local recording storage. */
  checksRecordingStorage?: boolean;
}

export function PoseSchedulePanel({
  poseState,
  schedule,
  variant = 'default',
  displayMode = 'full',
  isExpanded = true,
  onExpandedChange,
  onRestart,
  onNewPose,
  onPlay,
  onPause,
  onJumpTo,
  isEditable = true,
  checksRecordingStorage = false,
}: PoseSchedulePanelProps) {
  const { modelParticipantIds } = useRoom();
  // Schedule comes from unified session state - single source of truth
  const currentIndex = poseState.currentPose;
  const isActive = poseState.isActive;
  const isUntimedBreak = poseState.isUntimedBreak;
  const isPaused = poseState.isPaused || isUntimedBreak;

  // Can play if: not active OR paused/in break (works with or without schedule)
  const canPlay = !isActive || isPaused;
  // Can pause if: active and not already paused/in break
  const canPause = isActive && !isPaused;
  const playControlLabel = isActive
    ? isUntimedBreak
      ? 'End break'
      : 'Resume pose'
    : schedule.length > 0
    ? 'Start pose'
    : 'Start untimed pose';

  // Timer display values
  const isBreak = poseState.isBreak;
  const displayTime = poseState.isUntimed ? poseState.elapsed : poseState.remaining;
  const minutes = Math.floor(displayTime / 60);
  const seconds = displayTime % 60;
  const progress = poseState.duration > 0 ? ((poseState.duration - poseState.remaining) / poseState.duration) * 100 : 0;
  const statusPosition = getPoseScheduleStatusPosition({
    schedule,
    currentIndex,
    totalPoses: poseState.totalPoses,
  });
  const isModelMobile = variant === 'modelMobile';
  const shellClassName = isModelMobile
    ? 'flex flex-col bg-panel-secondary'
    : 'flex flex-col flex-1 border-b border-line bg-panel-secondary min-h-0';
  const mobileTimerTone = isActive
    ? isBreak || isUntimedBreak
      ? 'bg-[#293747] text-white border-[#293747]'
      : 'bg-[#d86637] text-white border-[#d86637]'
    : 'bg-panel text-text border-line';
  const timerBarClassName = isModelMobile
    ? `px-3 py-2.5 gap-2 flex items-center border-b relative ${mobileTimerTone}`
    : `px-4 py-3 gap-3 flex items-center border-b border-line relative ${isActive ? (isBreak ? 'bg-panel-secondary' : 'bg-accent-soft') : 'bg-panel'}`;
  const timerControlClassName = isModelMobile
    ? 'w-10 h-10 rounded-lg flex items-center justify-center bg-accent text-white hover:bg-accent-deep transition-colors shadow-sm flex-shrink-0'
    : 'w-8 h-8 rounded-lg flex items-center justify-center bg-accent text-white hover:bg-accent-deep transition-colors shadow-sm';
  const pauseControlClassName = isModelMobile
    ? 'w-10 h-10 rounded-lg flex items-center justify-center bg-panel border border-line text-text hover:bg-bg-secondary transition-colors flex-shrink-0'
    : 'w-8 h-8 rounded-lg flex items-center justify-center bg-panel border border-line text-text hover:bg-bg-secondary transition-colors';
  const actionButtonClassName = isModelMobile
    ? 'h-9 px-3 rounded text-[12px] font-semibold text-text-dim hover:text-text hover:bg-bg-secondary transition-colors'
    : 'h-5 px-1.5 rounded text-[10px] font-medium text-text-dim hover:text-text hover:bg-bg-secondary transition-colors';
  const visibleSchedule = getPoseScheduleEntries({
    schedule,
    currentIndex,
    isExpanded,
    isModelMobile,
  });
  const storageEstimate = useRecordingStorageEstimate(schedule, modelParticipantIds.length, checksRecordingStorage);
  const showStorageWarning = checksRecordingStorage && storageEstimate.status !== 'ok';
  const showTimerBar = displayMode !== 'scheduleOnly';
  const showSchedule = displayMode !== 'timerOnly';

  return (
    <div className={shellClassName}>
      {/* Timer Bar */}
      {showTimerBar && (
        <div className={timerBarClassName}>
          {/* Progress bar - only for timed poses */}
          {isActive && !poseState.isUntimed && (
            <div
              className={`absolute bottom-0 left-0 h-[2px] ${isModelMobile ? 'bg-white/60' : isBreak ? 'bg-text-dim' : 'bg-accent'}`}
              style={{ width: `${progress}%` }}
            />
          )}

          {/* Play/Pause controls */}
          {isEditable && (
            <div className="flex gap-1 flex-shrink-0">
              {canPlay && onPlay && (
                <button
                  onClick={onPlay}
                  className={timerControlClassName}
                  aria-label={playControlLabel}
                  title={playControlLabel}
                >
                  <svg width={isModelMobile ? '16' : '14'} height={isModelMobile ? '16' : '14'} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              )}
              {canPause && onPause && (
                <button
                  onClick={onPause}
                  className={pauseControlClassName}
                  aria-label="Pause pose"
                  title="Pause (call break)"
                >
                  <svg width={isModelMobile ? '16' : '14'} height={isModelMobile ? '16' : '14'} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Status and pose info */}
          <div className="flex-1 min-w-0">
            {isActive ? (
              <div className="flex items-center gap-2 min-w-0">
                {isModelMobile ? (
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase text-white/72">
                      {isBreak ? 'Break' : 'Pose'}{isPaused && !isUntimedBreak && ' paused'}
                    </div>
                    <div className="text-[14px] font-semibold text-white truncate">
                      {poseState.poseName}
                    </div>
                  </div>
                ) : (
                  <>
                    <span className={`pill ${isBreak ? 'pill-light' : 'pill-dark'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isBreak ? 'bg-text-dim' : 'bg-accent'} ${isPaused ? '' : 'animate-pulse'}`} />
                      {isBreak ? 'Break' : 'Pose'}{isPaused && !isUntimedBreak && ' (Paused)'}
                    </span>
                    <span className="text-[13px] text-text truncate min-w-0">
                      {poseState.poseName}
                    </span>
                  </>
                )}
                {statusPosition && (
                  <span className={`text-[11px] whitespace-nowrap ${isModelMobile ? 'text-white/72' : 'text-text-dim'}`}>
                    {statusPosition}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-[13px] text-text-dim">No pose active</span>
            )}
          </div>

          {/* Timer display */}
          {isActive && (
            <div className="flex items-baseline gap-1 flex-shrink-0">
              <span className={`font-mono ${isModelMobile ? 'text-2xl text-white' : 'text-xl text-text'} font-medium tabular-nums`}>
                {minutes}:{seconds.toString().padStart(2, '0')}
              </span>
              <span className={`text-[9px] uppercase ${isModelMobile ? 'text-white/64' : 'text-text-faint'}`}>
                {poseState.isUntimed ? 'elapsed' : 'left'}
              </span>
            </div>
          )}
        </div>
      )}

      {showSchedule && (
        <>
          {/* Header */}
          <div className={`${isModelMobile ? 'h-10 px-3' : 'h-[32px] px-4'} flex items-center justify-between border-b border-line-soft flex-shrink-0`}>
            <span className="text-[11px] text-text-dim font-medium uppercase">
              Schedule
            </span>
            {isEditable && (
              <div className="flex gap-1.5">
                {schedule.length > 0 && onRestart && (
                  <button
                    onClick={onRestart}
                    className={actionButtonClassName}
                    title="Restart from beginning"
                  >
                    ↺ Restart
                  </button>
                )}
                {onNewPose && (
                  <button
                    onClick={onNewPose}
                    className={`${actionButtonClassName} ${isModelMobile ? 'bg-accent text-white hover:bg-accent-deep hover:text-white' : ''}`}
                  >
                    + Edit schedule
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Untimed Break Banner */}
          {isUntimedBreak && (
            <div className={`${isModelMobile ? 'px-3' : 'px-4'} py-2 bg-panel-secondary border-b border-line flex items-center gap-3 flex-shrink-0`}>
              <span className="w-2 h-2 rounded-full bg-text-dim animate-pulse" />
              <span className="text-[12px] text-text-dim font-medium">Break in progress</span>
              {isEditable && (
                <span className="text-[10px] text-text-faint">Click play or a pose to continue</span>
              )}
            </div>
          )}

          {/* Schedule List */}
          <div className={isModelMobile ? 'overflow-visible' : 'flex-1 min-h-0 overflow-y-auto'}>
            {showStorageWarning && (
              <RecordingStorageWarning estimate={storageEstimate} isModelMobile={isModelMobile} />
            )}
            {schedule.length === 0 ? (
              <div className="p-4 text-center text-[12px] text-text-faint">
                No poses scheduled. Press play to start an untimed pose, or click "Edit" to create a schedule.
              </div>
            ) : (
              <div className="py-1">
                {visibleSchedule.map(({ item, index, displayNumber }) => (
                  <ScheduleItemRow
                    key={index}
                    item={item}
                    index={index}
                    displayNumber={displayNumber}
                    currentIndex={currentIndex}
                    isActive={isActive}
                    isUntimedBreak={isUntimedBreak}
                    isUntimed={poseState.isUntimed}
                    remaining={poseState.remaining}
                    elapsed={poseState.elapsed}
                    onJumpTo={isEditable && onJumpTo ? () => onJumpTo(index) : undefined}
                    isModelMobile={isModelMobile}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function RecordingStorageWarning({
  estimate,
  isModelMobile,
}: {
  estimate: RecordingStorageEstimate;
  isModelMobile: boolean;
}) {
  const estimatedStorage = formatStorageBytes(estimate.estimatedBytes);
  const availableStorage = formatStorageBytes(estimate.availableBytes);
  const cameraCopy = `${estimate.cameraCount} camera${estimate.cameraCount === 1 ? '' : 's'}`;
  const isRisky = estimate.status === 'risky';
  const toneClassName = isRisky
    ? 'border-warn/40 bg-warn/10 text-warn'
    : 'border-line bg-panel text-text-dim';

  let title = 'Recording storage could not be checked';
  let body = 'This browser cannot report how much storage lifedrawing.live can use. Make sure the model has plenty of free space before starting.';

  if (estimate.unavailableReason === 'untimed_poses') {
    body = 'This schedule includes untimed poses, so the recording size cannot be estimated. Keep plenty of free space and upload before closing the browser.';
  } else if (estimate.status === 'risky') {
    title = 'Recording storage may be too low';
    body = `This schedule may need about ${estimatedStorage} for ${cameraCopy} to create a reference pack, but this browser reports about ${availableStorage} available for lifedrawing.live.`;
  } else if (estimate.status === 'tight') {
    title = 'Recording storage may be tight';
    body = `This schedule may need about ${estimatedStorage} for ${cameraCopy} to create a reference pack, which is most of this browser's available storage for lifedrawing.live.`;
  }

  return (
    <div className={`${isModelMobile ? 'mx-3 my-2' : 'mx-4 my-2'} rounded-lg border px-3 py-2.5 ${toneClassName}`}>
      <div className="flex gap-2">
        <svg className="mt-0.5 h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
        </svg>
        <div className="min-w-0">
          <div className="text-[12px] font-semibold leading-snug">{title}</div>
          <div className="mt-1 text-[11px] leading-snug opacity-90">{body}</div>
        </div>
      </div>
    </div>
  );
}

interface ScheduleItemRowProps {
  item: ScheduleItem;
  index: number;
  displayNumber: number;
  currentIndex: number;
  isActive: boolean;
  isUntimedBreak: boolean;
  isUntimed: boolean;
  remaining: number;
  elapsed: number;
  onJumpTo?: () => void;
  isModelMobile?: boolean;
}

function ScheduleItemRow({
  item,
  index,
  displayNumber,
  currentIndex,
  isActive,
  isUntimedBreak,
  isUntimed,
  remaining,
  elapsed,
  onJumpTo,
  isModelMobile = false,
}: ScheduleItemRowProps) {
  const isPast = index < currentIndex;
  const isCurrent = index === currentIndex && isActive && !isUntimedBreak;
  const isTransition = isTransitionBreak(item);
  const isBreakItem = item.type === 'break';
  const currentRowToneClassName = isModelMobile
    ? isBreakItem || isTransition
      ? 'bg-[#293747]/12 border-l-2 border-[#293747]'
      : 'bg-[#d86637]/12 border-l-2 border-[#d86637]'
    : isTransition
      ? 'bg-panel-secondary border-l-2 border-text-dim'
      : 'bg-accent-soft border-l-2 border-accent';
  const currentIndicatorClassName = isModelMobile
    ? isBreakItem
      ? 'bg-[#293747] text-white'
      : 'bg-[#d86637] text-white'
    : 'bg-accent text-white';
  const itemTypeClassName = isModelMobile
    ? isBreakItem
      ? 'text-[#293747]'
      : 'text-[#d86637]'
    : isBreakItem
      ? 'text-text-dim'
      : 'text-accent';

  const formatDuration = (seconds: number) => {
    if (seconds === 0) return 'untimed';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
  };

  // For current item: show elapsed for untimed, remaining for timed
  const displayTime = isUntimed ? elapsed : remaining;

  const isClickable = !!onJumpTo;
  const jumpLabel = isTransition ? `Jump to step ${item.name}` : `Jump to pose ${item.name}`;

  return (
    <div
      className={`group ${isModelMobile ? 'px-3' : 'px-4'} ${isModelMobile ? (isTransition ? 'py-2' : 'py-3') : (isTransition ? 'py-1.5' : 'py-2')} flex items-center gap-3 transition-colors ${
        isClickable ? 'cursor-pointer hover:bg-bg-secondary' : ''
      } ${
        isCurrent
          ? currentRowToneClassName
          : isPast
          ? 'opacity-40'
          : ''
      }`}
      onClick={onJumpTo}
      onKeyDown={(event) => {
        if (!onJumpTo || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        onJumpTo();
      }}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={isClickable ? jumpLabel : undefined}
      title={isClickable ? `Click to jump to this ${isTransition ? 'step' : 'pose'}` : undefined}
    >
      {/* Index/Status indicator */}
      {isTransition ? (
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <span className={`w-1.5 h-1.5 rounded-full ${isCurrent ? 'bg-text-dim animate-pulse' : 'bg-line'}`} />
        </div>
      ) : (
        <div
          className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono flex-shrink-0 ${
            isCurrent
              ? currentIndicatorClassName
              : isPast
              ? 'bg-line text-text-faint'
              : 'bg-panel-secondary text-text-dim border border-line'
          }`}
        >
          {isPast ? '✓' : displayNumber}
        </div>
      )}

      {/* Item info */}
      <div className="flex-1 min-w-0">
        {isTransition ? (
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] uppercase text-text-faint">
              {item.name}
            </span>
            {isCurrent && (
              <span className="text-[9px] text-text-dim uppercase">
                in progress
              </span>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <span
                className={`text-[10px] font-mono uppercase ${itemTypeClassName}`}
              >
                {item.type}
              </span>
              {isCurrent && (
                <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isModelMobile && isBreakItem ? 'bg-[#293747]' : isModelMobile ? 'bg-[#d86637]' : 'bg-accent'}`} />
              )}
            </div>
            <div
              className={`text-[13px] truncate ${
                isCurrent ? 'text-text' : 'text-text-dim'
              }`}
            >
              {item.name}
            </div>
          </>
        )}
      </div>

      {/* Duration/Remaining/Elapsed */}
      <div className="text-right flex-shrink-0">
        {isCurrent ? (
          <span className={`font-mono ${isTransition ? 'text-[12px] text-text-dim' : 'text-[14px] text-text'} tabular-nums`}>
            {Math.floor(displayTime / 60)}:
            {(displayTime % 60).toString().padStart(2, '0')}
          </span>
        ) : (
          <span className={`font-mono ${isTransition ? 'text-[10px]' : 'text-[11px]'} text-text-faint`}>
            {formatDuration(item.duration)}
          </span>
        )}
      </div>

      {/* Jump indicator for non-current items (only when clickable) */}
      {isClickable && !isCurrent && !isPast && (
        <div className="w-4 h-4 flex items-center justify-center text-text-faint opacity-0 group-hover:opacity-100">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}
    </div>
  );
}
